import React from 'react';
import { ExternalLink, Github } from 'lucide-react';

interface FeaturedProject {
  id: number;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  liveUrl?: string;
  githubUrl?: string;
}

const FeaturedProjects: React.FC = () => {
  const featuredProjects: FeaturedProject[] = [
    {
      id: 1,
      title: 'Productivity App',
      description: 'A full-stack productivity app with a genAI twist.',
      image: '/productivity_app.png',
      technologies: ['React', 'TypeScript', 'Large Language Models'],
      liveUrl: 'https://app.michaelguevarra.com',
      githubUrl: 'https://github.com/mike22g',
    },
    {
      id: 2,
      title: '<PROJECT_L>',
      description: 'Work in progress',
      image: '/api/placeholder/400/300',
      technologies: ['Unity', 'C#', 'VR'],
    },
    {
      id: 3,
      title: '<PROJECT_Z>',
      description: 'Work in progress',
      image: '/api/placeholder/400/300',
      technologies: ['React', 'Node.js', 'Large Language Models', 'three.js', 'phaser'],
    },
  ];

  return (
    <section id="featured-projects" className="section-padding">
      <div className="container-max">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
            Featured Projects
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Here are some of my recent projects that showcase my skills and passion for creating
            innovative digital solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {featuredProjects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="aspect-video bg-secondary-200 flex items-center justify-center overflow-hidden">
                {project.image.startsWith('/api/placeholder') ? (
                  <span className="text-secondary-500">Project Image</span>
                ) : (
                  <img
                    src={project.image}
                    alt={`Preview of ${project.title}`}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-secondary-900 mb-2">
                  {project.title}
                </h3>
                <p className="text-secondary-600 mb-4 line-clamp-3">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex gap-3">
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Live Demo
                    </a>
                  )}
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-secondary-600 hover:text-secondary-700 font-medium"
                    >
                      <Github className="h-4 w-4 mr-1" />
                      Code
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects; 